import { useState } from "react";
import { Loader2, FileCheck2, ClipboardCheck, ShieldAlert, Clock, XCircle, CheckCircle2 } from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";
import api from "../lib/api";

/**
 * Pannello unificato per la "richiesta autorizzazione esame":
 *  - Bando chiuso → banner rosa
 *  - Bando aperto, no richiesta → invito a inviare la richiesta (modale motivazione)
 *  - Richiesta pending → banner ambra "in attesa di HR"
 *  - Richiesta rejected → banner rosso con la nota del rifiuto + possibilità di rimandare
 *
 * Props:
 *  - sessionInfo: oggetto restituito da GET /quiz/questions (block_reason, session, auth_status, auth_request, ...)
 *  - tierLabel: stringa "Vice Sceriffo" | "Sergente" | ecc.
 *  - nextRole: prossimo grado
 *  - onAuthApproved: callback dopo richiesta inviata (per refresh)
 */
export function ExamAuthRequestPanel({ sessionInfo, tierLabel, nextRole, onRefresh }) {
  const [open, setOpen] = useState(false);
  const [motiv, setMotiv] = useState("");
  const [sending, setSending] = useState(false);
  const session = sessionInfo?.session || {};
  const block = sessionInfo?.block_reason;
  const authStatus = sessionInfo?.auth_status || sessionInfo?.auth_request?.status;
  const req = sessionInfo?.auth_request;

  const submit = async () => {
    if (motiv.trim().length < 10) {
      toast.error("Motivazione richiesta (min 10 caratteri)");
      return;
    }
    setSending(true);
    try {
      await api.post("/exam-auth/request", { motivazione: motiv.trim() });
      toast.success("Richiesta inviata. HR la valuterà a breve.", { duration: 8000 });
      setOpen(false);
      setMotiv("");
      if (onRefresh) await onRefresh();
    } catch (e) {
      toast.error(`Errore: ${e?.response?.data?.detail || "richiesta fallita"}`);
    } finally {
      setSending(false);
    }
  };

  // Bando chiuso
  if (block === "session_closed") {
    return (
      <div className="bg-rose-500/10 border border-rose-500/30 rounded-xl p-5" data-testid="exam-auth-bando-closed">
        <div className="font-heading font-bold text-rose-300 mb-1 flex items-center gap-2">
          <XCircle className="h-4 w-4" /> Bando {tierLabel} non aperto
        </div>
        <p className="text-sm text-[#D4C480]">
          Al momento il Comando non ha aperto il bando per la promozione a <span className="font-semibold text-sheriff-gold">{nextRole}</span>.
          Le richieste di autorizzazione esame saranno disponibili quando il bando verrà aperto.
        </p>
      </div>
    );
  }

  // Richiesta approvata: se ci sono step HR non ancora tutti completati → banner iter
  if (authStatus === "approved" && block === "hr_steps_pending") {
    const steps = req?.hr_steps || [];
    const done = req?.hr_steps_completed || 0;
    const total = req?.hr_steps_total || steps.length;
    return (
      <div className="bg-amber-400/10 border border-amber-400/40 rounded-xl p-5" data-testid="exam-auth-steps-pending">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="font-heading font-bold text-amber-200 flex items-center gap-2">
            <ClipboardCheck className="h-4 w-4" /> Iter HR in corso — {done}/{total} step completati
          </div>
          <span className="text-[10px] font-mono uppercase tracking-widest bg-amber-500/15 border border-amber-500/40 text-amber-200 rounded-full px-2 py-0.5">
            In attesa
          </span>
        </div>
        <p className="text-sm text-[#D4C480] mb-4">
          La tua richiesta per l&apos;esame <span className="font-semibold text-sheriff-gold">{tierLabel}</span> è
          stata <span className="text-emerald-300 font-semibold">approvata</span>, ma devi ancora completare l&apos;iter con le Risorse Umane.
          Contatta HR per programmare gli step qui sotto — appena saranno tutti spuntati, potrai sostenere l&apos;esame.
        </p>
        <div className="space-y-2">
          {steps.map((s, idx) => (
            <div
              key={idx}
              className={`flex items-start gap-3 rounded-lg px-3 py-2 border ${
                s.completed ? "border-emerald-400/40 bg-emerald-500/10" : "border-sheriff-gold/20 bg-[#0A1F18]/40"
              }`}
              data-testid={`exam-auth-candidate-step-${idx}`}
            >
              {s.completed ? (
                <CheckCircle2 className="h-4 w-4 text-emerald-300 mt-0.5 shrink-0" />
              ) : (
                <div className="h-4 w-4 rounded-full border border-amber-300/60 mt-0.5 shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className={`text-sm font-medium ${s.completed ? "text-emerald-200" : "text-[#F5E6B8]"}`}>
                  {s.name}
                </div>
                {s.description && (
                  <div className="text-xs text-[#B8A269] mt-0.5">{s.description}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Richiesta approvata: il chiamante mostrerà l'UI esame (questo componente non rende nulla)
  if (authStatus === "approved" && block !== "auth_required") return null;

  // Richiesta pending
  if (authStatus === "pending") {
    return (
      <div className="bg-amber-400/10 border border-amber-400/40 rounded-xl p-5" data-testid="exam-auth-pending">
        <div className="font-heading font-bold text-amber-200 mb-2 flex items-center gap-2">
          <Clock className="h-4 w-4 animate-pulse" /> Richiesta in attesa di approvazione HR
        </div>
        <p className="text-sm text-[#D4C480] mb-3">
          La tua richiesta per l&apos;esame <span className="font-semibold text-sheriff-gold">{tierLabel}</span> è stata inviata
          alle <span className="font-semibold text-amber-200">Risorse Umane</span>. Riceverai una notifica quando verrà valutata.
        </p>
        {req?.requested_at && (
          <div className="text-xs text-[#B8A269]">
            Inviata: <span className="font-mono">{new Date(req.requested_at).toLocaleString("it-IT")}</span>
          </div>
        )}
        {req?.motivazione && (
          <div className="mt-3 bg-[#0A1F18]/60 border border-amber-400/20 rounded-lg p-3">
            <div className="text-[10px] uppercase-label text-amber-300/80 mb-1">La tua motivazione</div>
            <div className="text-sm text-[#E8D89A] whitespace-pre-wrap">{req.motivazione}</div>
          </div>
        )}
      </div>
    );
  }

  // Richiesta rifiutata
  if (authStatus === "rejected") {
    return (
      <div className="bg-rose-500/10 border border-rose-500/40 rounded-xl p-5" data-testid="exam-auth-rejected">
        <div className="font-heading font-bold text-rose-300 mb-2 flex items-center gap-2">
          <XCircle className="h-4 w-4" /> Richiesta rifiutata
        </div>
        <p className="text-sm text-[#D4C480] mb-3">
          La tua richiesta per l&apos;esame <span className="font-semibold text-sheriff-gold">{tierLabel}</span> è stata
          <span className="font-semibold text-rose-300"> rifiutata</span> dalle Risorse Umane.
        </p>
        {req?.decision_note && (
          <div className="bg-[#0A1F18]/60 border border-rose-400/20 rounded-lg p-3 mb-3">
            <div className="text-[10px] uppercase-label text-rose-300/80 mb-1">Nota HR</div>
            <div className="text-sm text-[#E8D89A] whitespace-pre-wrap">{req.decision_note}</div>
          </div>
        )}
        <Button onClick={() => setOpen(true)} className="bg-sheriff-gold hover:bg-sheriff-gold-dark text-[#0A1F18] font-bold uppercase tracking-[0.12em] rounded-full px-5" data-testid="exam-auth-retry-btn">
          <FileCheck2 className="h-4 w-4 mr-2" /> Invia nuova richiesta
        </Button>
        <ExamAuthDialog open={open} setOpen={setOpen} motiv={motiv} setMotiv={setMotiv} submit={submit} sending={sending} tierLabel={tierLabel} />
      </div>
    );
  }

  // Bando aperto, nessuna richiesta
  return (
    <div className="bg-emerald-500/10 border border-emerald-500/40 rounded-xl p-5" data-testid="exam-auth-request">
      <div className="font-heading font-bold text-emerald-300 mb-2 flex items-center gap-2">
        <ShieldAlert className="h-4 w-4" /> Bando {tierLabel} aperto · Richiesta autorizzazione necessaria
      </div>
      <p className="text-sm text-[#D4C480] mb-4">
        Per accedere all&apos;esame di <span className="font-semibold text-sheriff-gold">{nextRole}</span> devi prima inviare
        una richiesta di autorizzazione alle <span className="font-semibold text-amber-200">Risorse Umane</span>.
        Solo dopo approvazione potrai iniziare il quiz.
      </p>
      {session?.slots > 0 && (
        <div className="text-xs text-[#B8A269] mb-3">
          Posti disponibili: <span className="text-sheriff-gold font-bold">{Math.max(session.slots - (session.promoted_count || 0), 0)}</span> di {session.slots}
        </div>
      )}
      <Button onClick={() => setOpen(true)} className="bg-sheriff-gold hover:bg-sheriff-gold-dark text-[#0A1F18] font-bold uppercase tracking-[0.12em] rounded-full px-5" data-testid="exam-auth-request-btn">
        <ClipboardCheck className="h-4 w-4 mr-2" /> Richiedi autorizzazione
      </Button>
      <ExamAuthDialog open={open} setOpen={setOpen} motiv={motiv} setMotiv={setMotiv} submit={submit} sending={sending} tierLabel={tierLabel} />
    </div>
  );
}

function ExamAuthDialog({ open, setOpen, motiv, setMotiv, submit, sending, tierLabel }) {
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { setOpen(false); setMotiv(""); } }}>
      <DialogContent className="bg-[#0F2620] border-sheriff-gold/40 max-w-md" data-testid="exam-auth-dialog">
        <DialogHeader>
          <DialogTitle className="text-sheriff-gold font-heading flex items-center gap-2">
            <FileCheck2 className="h-5 w-5" /> Richiesta autorizzazione · {tierLabel}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-[#D4C480]">
            Compila la motivazione (min 10 caratteri). HR riceverà la tua richiesta e la valuterà.
          </p>
          <div>
            <label className="block uppercase-label text-[10px] text-sheriff-gold mb-1.5">Motivazione</label>
            <textarea
              value={motiv}
              onChange={(e) => setMotiv(e.target.value)}
              rows={5}
              placeholder="Es. Sono pronto per la promozione, ho completato XX turni di servizio e voglio assumere maggiori responsabilità nel coordinamento delle pattuglie..."
              className="w-full bg-[#0A1F18] border border-sheriff-gold/30 rounded-lg px-3 py-2 text-sm text-[#F5E6B8] placeholder:text-[#8B7B49] focus:outline-none focus:border-sheriff-gold"
              data-testid="exam-auth-motivation-input"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button onClick={() => { setOpen(false); setMotiv(""); }} variant="ghost" className="text-[#D4C480]">Annulla</Button>
            <Button onClick={submit} disabled={sending || motiv.trim().length < 10} className="bg-sheriff-gold hover:bg-sheriff-gold-dark text-[#0A1F18] font-bold disabled:opacity-50" data-testid="exam-auth-submit-btn">
              {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ClipboardCheck className="h-4 w-4 mr-2" />}
              Invia richiesta
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ExamAuthRequestPanel;
